import {
  MessageUnionType,
  RunOutputSchema,
  RunSchema,
} from '../../../types/schema';
import {
  getFlattenedArrayOfGenerations,
  getMessageContentAsText,
  getMessageFields,
  getMessageType,
  isMessageLike,
  maybeGetArrayOfMessages,
} from '../../../utils/messages';

function stringifyMessages(messages: MessageUnionType[]) {
  return messages
    ?.map((message) => {
      const type = getMessageType(message);
      const additionalKwargs = getMessageFields(message)?.additional_kwargs;

      const content =
        getMessageContentAsText(message) ||
        (additionalKwargs != null &&
        typeof additionalKwargs === 'object' &&
        Object.keys(additionalKwargs).length > 0
          ? JSON.stringify(additionalKwargs)
          : '');

      if (!type && content === '') return '';
      return `${type}: ${content || '""'}`;
    })
    .filter((x) => x !== '')
    .join('\n');
}

export function filterOutputs(
  outputs: RunSchema['outputs'] | string | null | undefined,
  inputs: RunSchema['inputs'] | string | null | undefined,
  options?: { removeKeys?: string[]; showEmpty?: boolean }
) {
  return (
    filterInputs(outputs, {
      removeKeys: Object.keys(inputs ?? {}).concat(['input'], ['inputs']),
      ...options,
    }) || filterInputs(outputs, options)
  );
}

export function filterInputs(
  data: unknown,
  options?: {
    removeKeys?: string[];
    showEmpty?: boolean;
    heuristicCompactViewDisplayKey?: string;
  }
): string | null | undefined {
  if (!data) return options?.showEmpty ? (data as null | undefined) : undefined;
  if (
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean'
  )
    return data.toString();

  let keys = Object.keys(data);
  if (options?.removeKeys) {
    const toRemove = new Set(options.removeKeys);
    keys = keys.filter((key) => !toRemove.has(key));
  }

  if (keys.length === 0) return options?.showEmpty ? '{}' : '';

  const generations = getFlattenedArrayOfGenerations(data as RunOutputSchema);
  if (
    generations.length > 0 &&
    generations.every(
      (generation) => typeof generation === 'object' && generation !== null
    )
  ) {
    const firstGeneration = generations[0];
    if ('message' in firstGeneration) {
      return stringifyMessages(
        generations.map((g) => g.message) as MessageUnionType[]
      );
    } else if ('text' in firstGeneration) {
      return generations.map((g) => g.text).join('\n');
    }
  } else if (isMessageLike(data)) {
    return stringifyMessages([data]);
  } else if (keys.length === 1) {
    const key = keys[0];
    const value: unknown = data[key];

    const maybeMessagesArray = maybeGetArrayOfMessages(value);
    if (maybeMessagesArray) {
      // Chat Model Runs
      return stringifyMessages([
        maybeMessagesArray[maybeMessagesArray.length - 1],
      ]);
      // Check if key is 'prompt' and the value is a string
    } else if (key === 'prompt' && typeof value === 'string') {
      // LLM Runs
      return value;
    } else if (typeof value !== 'string') {
      return JSON.stringify(value);
    } else {
      return value;
    }
  } else if (keys.length > 1) {
    if (options?.heuristicCompactViewDisplayKey) {
      const heuristicValue = data[options.heuristicCompactViewDisplayKey];
      return typeof heuristicValue === 'string'
        ? heuristicValue
        : JSON.stringify(heuristicValue);
    }
    // look for the first single message or string
    for (const key of keys) {
      const value = data[key];
      if (isMessageLike(value)) {
        return stringifyMessages([value]);
      } else if (typeof value === 'string' && value !== '') {
        return value;
      }
    }
    // if none found, look for the first array of messages
    for (const key of keys) {
      const value = data[key];
      const maybeMessagesArray = maybeGetArrayOfMessages(value);
      if (maybeMessagesArray) {
        return stringifyMessages([
          maybeMessagesArray[maybeMessagesArray.length - 1],
        ]);
      }
    }
    // if none found, look for first number
    for (const key of keys) {
      const value = data[key];
      if (typeof value === 'number') {
        return `${value}`;
      }
    }
  }

  return JSON.stringify(data);
}
